<template>
  <div class="momentum-menu">
    <item-header
      type="momentum"
      :label="$t('labels.momentumOf')"
      @close="$emit('close')"
    >
      <template #content>
        <button-item
          v-if="ctx.element"
          class="mt-1"
          @click="select(ctx.element)"
          @pointerover="setHighlight(ctx.element)"
          @pointerout="removeHighlight(ctx.element)"
        >
          {{ ctx.element?.title }}
        </button-item>
      </template>
    </item-header>

    <!-- GENERAL -->
    <drawer
      :title="$t('labels.general')"
      :open="drawerStates.momentumGeneral"
      @update:open="drawerStates.momentumGeneral = $event"
    >
      <!-- description -->
      <label class="label">{{ $t("labels.description") }}</label>
      <input-text-editor
        :value="ctx.momentum.description"
        :placeholder="$t('labels.description')"
        @update:value="updateKey('description', $event)"
      />
    </drawer>

    <!-- INFLUENCE CURVE -->
    <drawer
      :title="$t('labels.value')"
      :open="drawerStates.momentumCurve"
      @update:open="drawerStates.momentumCurve = $event"
    >
      <div class="column" v-if="ctx.element">
        <section>
          <div class="line-docanchor">
            <label class="label">{{ $t("labels.graph") }}</label>
            <doc-anchor :object="ctx.momentum" prop="curve" />
          </div>
          <input-curve
            :value="ctx.element.value"
            :x-min="ctx.element.minValue"
            :x-max="ctx.element.maxValue"
            :point-list="curvePoints"
            :type="curveType"
            :fixed-start="false"
            :formula="ctx.momentum.formula"
            class="mt-1"
            @change="onGraphChange"
            @update:formula="updateKey('formula', $event)"
            @hovervalue="updateCurveHover($event)"
          />
          <p>
            {{ $t("labels.xAxis") }}{{ $t(":") }}
            {{ $t("labels.valueOfX", [ctx.element.title || $t("labels.unnamed")])
            }}<br />
            {{ $t("labels.yAxis") }}{{ $t(":") }}
            {{
              $t("labels.influenceOnX", [ctx.element.title || $t("labels.unnamed")])
            }}
          </p>
          <i18n-t
            keypath="texts.influenceCurveConstantExample"
            tag="p"
            scope="global"
          >
            <template #startElement>
              <em>{{ ctx.element.title || $t("labels.unnamed") }}</em>
            </template>
            <template v-if="ctx.element.unit" #startUnit
              > {{ ctx.element.unit }}
            </template>
            <template #endElement>
              <em>{{ ctx.element.title || $t("labels.unnamed") }}</em>
            </template>
            <template #startValue>{{ curveHover.x }}</template>
            <template #impulseValue>
              {{ $n(curveHover.y || 0, "displaySign") }}
            </template>
            <template v-if="ctx.element.unit" #endUnit
              > {{ ctx.element.unit }}
            </template>
          </i18n-t>
        </section>
      </div>
    </drawer>

    <!-- INTERVAL -->
    <drawer :title="$t('labels.interval')">
      <div class="line-docanchor">
        <label class="label">
          {{ $t("labels.waitTimeBetweenRounds") }}{{ $t(":") }}
        </label>
        <doc-anchor :object="ctx.momentum" prop="interval" />
      </div>
      <input-number
        :value="ctx.momentum.interval"
        :min="1"
        @update:value="updateKey('interval', $event)"
      />
    </drawer>

    <drawer v-if="features.design" :title="$t('labels.design')">
      <input-image
        :id="ctx.momentum.imageId || undefined"
        :max-width="1000"
        :max-height="1000"
        :compression="0.9"
        :control-object-fit="true"
        @update="updateImage"
        @delete="deleteImage"
      />
    </drawer>

    <menu-group class="mt-auto">
      <button
        class="button button_secondary mv-2"
        type="button"
        @click="deleteMomentum"
      >
        {{ $t("actions.deleteMomentum", 1) }}
      </button>
    </menu-group>
  </div>
</template>

<script setup lang="ts">
import { computed, reactive } from "vue";

import Drawer from "@core/ui/DrawerBlock.vue";

import MenuGroup from "@core/ui/MenuGroup.vue";
import ButtonItem from "@core/input/ButtonItem.vue";
import InputNumber from "@core/input/InputNumber.vue";
import InputTextEditor from "@core/input/TextEditor.vue";

import InputImage from "@editor/items/image/InputImage.vue";
import ItemHeader from "@editor/components/ItemHeader.vue";
import DocAnchor from "@editor/ui/DocumentationAnchor.vue";
import InputCurve from "@editor/items/relation/InputCurve.vue";

import { useMomentumStore } from "@editor/items/momentum/momentums.store";
import { useSelectionStore } from "@editor/store/editor/selection.store";
import { useDrawerStates } from "@editor/store/editor/drawerStates.store";
import { useFeatures } from "@editor/store/features.store";
import { useMomentum } from "./useMomentum";

import type { MomentumItem } from "./MomentumItem";
import type { CurveFormType } from "../relation/RelationItem";
import type { GraphPoint } from "../relation/GraphPoint";

interface Props {
  momentumId: number;
}
const props = defineProps<Props>();

defineEmits<{
  (event: "close"): void;
}>();

const momentumStore = useMomentumStore();

const drawerStates = useDrawerStates();
const features = useFeatures();

const { select, setHighlight, removeHighlight } = useSelectionStore();

const ctx = useMomentum(() => props.momentumId);

// ======================
// CURVE

const curveHover = reactive({
  x: 0,
  y: 0,
  x2: 0,
  y2: 0
});

const curvePoints = computed(() => ctx.momentum.curvePoints);
const curveType = computed(() => ctx.momentum.curveType);

function updateCurveHover(hoverData: {
  x: number;
  y: number;
  x2: number;
  y2: number;
}) {
  curveHover.x = hoverData.x;
  curveHover.y = hoverData.y;
  curveHover.x2 = hoverData.x2;
  curveHover.y2 = hoverData.y2;
}

function onGraphChange(data: { points: GraphPoint[]; type: CurveFormType }) {
  momentumStore.updateCurve({
    id: props.momentumId,
    curvePoints: data.points,
    curveType: data.type
  });
}

// ======================
// UPDATE

function updateKey<K extends keyof MomentumItem>(
  key: K,
  value: MomentumItem[K]
) {
  return momentumStore.updateKey(props.momentumId, key, value);
}

// ======================
// IMAGE

function updateImage(image: { id: number }) {
  return ctx.updateKey("imageId", image.id);
}

function deleteImage() {
  return ctx.updateKey("imageId", null);
}

function deleteMomentum() {
  return ctx.delete();
}
</script>

<style lang="scss">
.momentum-menu {
  display: flex;
  flex-direction: column;
  min-height: 100%;
}
</style>
